import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, NEVER, Observable, of } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';
import { IFunnelValidations, IFunnelRequest, IFunnelFlowResponse } from 'src/app/domain/interfaces/funnel/interface-funnel';
import { AlertPosition, AlertType } from 'src/app/domain/models/alert/alert-type-enum';
import { FunnelStep } from 'src/app/domain/models/funnel/funnel-step-enum';
import { FunnelGateway } from 'src/app/domain/models/funnel/gateway/funnel-gateway';
import { environment } from 'src/environments/environment';
import { AlertService } from '../alert-service/alert.service';
import { TokenService } from '../user-service/token.service';
import { CypherService } from '../cypher-service/cypher.service';
import { IFunnelResponse } from 'src/app/domain/interfaces/funnel/interface-funnel-response';
import { ICaptchaSettingsResponse, IValidateCaptchaResponse } from 'src/app/domain/interfaces/funnel/interface-captcha';
import { FunnelFlow } from 'src/app/domain/models/funnel/register-funnels/funnel-flow-register';
import { RegisterProcessesType } from 'src/app/domain/models/funnel/register-funnels/register-processes-type-enum';
import { FunnelCodeResponse } from 'src/app/domain/models/funnel/funnel-code-response-enum';
import { getCreditCardValidationMessage, isCreditCardValidationError } from 'src/app/domain/models/funnel/credit-card-validation-error';
import { UserInfoSentType } from 'src/app/domain/models/funnel/register-funnels/info-sent-type-enum';
import { UserRegister } from 'src/app/domain/models/funnel/register-funnels/user-register';
import { ReportBinService } from '../report-bin-service/report-bin.service';
import { LoadingType } from 'src/app/domain/models/alert/loading-type-enum';
import { BINRulesService } from '../creditcard-service/bin-rules.service';
import { Brand } from 'src/app/domain/models/funnel/brand-enum';
import { RemarketingService } from '../remarketing-service/remarketing.service';
import { EventEverFlowType } from 'src/app/domain/models/funnel/register-funnels/event-everflow-type-enum';
import { MemberAccessLevel } from 'src/app/domain/models/users/member-access-level-enum';
import { FeatureFlagsService } from '../feature-flags-service/feature-flags.service';
import { PartialSaveTrigger } from 'src/app/domain/models/funnel/register-funnels/partial-save-trigger-enum';

const user_register_storage = "user-register";
// URL params carried over when an expired registration restarts under the internal affiliate:
// the offer plus the user's prefill data. Everything else belongs to the original affiliate.
const INTERNAL_RESTART_KEPT_PARAMS = ['oid', 'fname', 'lname', 'email', 'phone', 'address', 'city', 'state', 'zipcode', 'province', 'postalcode'];
const INTERNAL_RESTART_CLICK_TIMEOUT_MS = 5000;
@Injectable({
  providedIn: 'root'
})
export class FunnelService extends FunnelGateway {

  private funnelFlowSubject!: BehaviorSubject<Object>;
  private restartingExpiredRegistration = false;
  private hideHeaderAndFooterSource = new BehaviorSubject<boolean>(false);
  hideHeaderAndFooter$ = this.hideHeaderAndFooterSource.asObservable()

  constructor(private http: HttpClient, private tokenService: TokenService, private cypherService: CypherService, private alertService: AlertService, private reportBlockBinService: ReportBinService, private binRulesService: BINRulesService, private remarketingService: RemarketingService, private injector: Injector) {
    super();
    this.funnelFlowSubject = new BehaviorSubject<Object>(
      this.cypherService.DecryptObject(localStorage.getItem(user_register_storage) ?? "")
    );
  }

  // Resolved through the Injector, not a constructor param, so FeatureFlagsService (and its
  // eager /feature-flags GET, fired from its own constructor) is only instantiated the first
  // time a gated method below actually runs -- not merely because something injected
  // FunnelService. FunnelService is constructor-injected by nearly every funnel component, so a
  // constructor param here would fire that GET for every spec that creates one of those
  // components, whether or not it ever exercises this feature.
  private get featureFlagsService(): FeatureFlagsService {
    return this.injector.get(FeatureFlagsService);
  }

  // 'partial-save-trigger-on-step1' is the mandatory outermost gate for Asana 1218515159227367:
  // OFF (including a failed /feature-flags request, which FeatureFlagsService.refresh resolves
  // to {}) means every client-side path this repo owns below -- SendRegisterForm's Step1Completed
  // stamp/SaveUser append and sendPartialCustomerToRemarketingService's prepop suppression -- is
  // byte-for-byte today's behaviour. first() avoids a leaking subscription; ready$ inside
  // FeatureFlagsService guarantees this emits the real flag value (or the fail-closed {} default).
  // This client-side read is independent of the server-side flag read registration-api makes for
  // its own gate: if this repo's read fails/resolves OFF while the API's resolves ON on an
  // OnStep1Complete row, the client never sets Step1Completed and the API's gate then suppresses
  // blur saves too, so the partial is silently never written. That divergence can only be closed
  // from the API side (e.g. it should not defer to a client flag it can't itself observe) -- fixing
  // it is out of scope for this repo.
  private isPartialSaveTriggerFlagEnabled$(): Observable<boolean> {
    return this.featureFlagsService.isEnabled('partial-save-trigger-on-step1').pipe(first());
  }

  // Affiliate-level configuration takes precedence over funnel-level (REQ-02); NULL/undefined at
  // both levels resolves to OnEntry, preserving today's behaviour for every unconfigured funnel
  // (REQ-03/REQ-06). Reads values that already live on FunnelFlow, so it survives the
  // encrypted-localStorage FunnelFlow restore.
  private ResolvePartialSaveTrigger(funnelFlow?: FunnelFlow): PartialSaveTrigger {
    return funnelFlow?.EverflowAffiliate?.partialSaveTrigger
      ?? funnelFlow?.SubscriptionProduct?.funnel?.partialSaveTrigger
      ?? PartialSaveTrigger.OnEntry;
  }

  public ValidateFunnel(funnelRequest: IFunnelRequest): Observable<IFunnelResponse> {
    return this.http.post<IFunnelResponse>(`${environment.apiUrl}/api/funnel/validatefunnel`, funnelRequest).pipe(map((funnelResponse: IFunnelResponse) => {
      return funnelResponse;
    }));
  }

  public GetCaptchaSettings(offerId: string): Observable<ICaptchaSettingsResponse> {
    return this.http.post<ICaptchaSettingsResponse>(`${environment.apiUrl}/api/funnel/getcaptchasettings`, { OfferId: offerId });
  }

  public ValidateCaptcha(offerId: string, reCaptchaToken: string): Observable<IValidateCaptchaResponse> {
    return this.http.post<IValidateCaptchaResponse>(`${environment.apiUrl}/api/funnel/validatecaptcha`, { OfferId: offerId, ReCaptchaToken: reCaptchaToken });
  }

  sendEverflowClick() {
    var offerId = window.EF.urlParameter('oid');
    var transactionId = window.EF.getTransactionId(offerId);
    console.log('Previous EF transactionId:', transactionId);
    if (!transactionId) {
      window.EF.click({
        offer_id: offerId,
        affiliate_id: window.EF.urlParameter('ts'),
        sub1: window.EF.urlParameter('sub1'),
        sub2: window.EF.urlParameter('gclid'),
        sub3: window.EF.urlParameter('sub3'),
        sub4: window.EF.urlParameter('sub4'),
        sub5: window.EF.urlParameter('sub5'),
        uid: window.EF.urlParameter('uid'),
        source_id: window.EF.urlParameter('source_id'),
        transaction_id: window.EF.urlParameter('_ef_transaction_id'),
      })
      .then((transactionId: string) => {
        // transactionId containts the unique Everflow transaction ID
        console.log('New EF transactionId:', transactionId);
      });
    }
  }
  
  sendEverflowStepEvent(offerId: string, event: EventEverFlowType) {
    window.EF.conversion({
      offer_id: offerId,
      adv_event_id: event
    })
    .then((response: string) => {
      console.log('EF event response:', response);
    });
  }

  sendEverflowSaleEvent(offerId: string) {
    window.EF.conversion({
      offer_id: offerId,
    })
    .then((response: string) => {
      console.log('EF conversion response:', response);
    });
  }

  public GetUserRegister(): Observable<any> {
    return this.funnelFlowSubject.asObservable().pipe();
  }

  public ClearUserRegister(FunnelFlow?: FunnelFlow) {
    this.tokenService.clearStorage();
    FunnelFlow && this.funnelFlowSubject.next(FunnelFlow);
  }

  // Clears the customer-identity fields on a UserRegister. Used when switching funnels/providers
  // so an id issued under a different offer (e.g. VRIO -> Konnektive) is not reused.
  private ResetUserIdentity(userTemporary?: UserRegister) {
    if (!userTemporary) return;
    userTemporary.crmCustomerId = undefined;
    userTemporary.crmCustomerIdOfferId = undefined;
    userTemporary.orderId = undefined;
    userTemporary.userId = undefined;
    userTemporary.userTemporaryId = undefined;
  }

  // Drops a crmCustomerId/orderId that was issued under a different funnel/offer so it is never
  // re-sent when re-registering in another funnel/provider. The backend then resolves/creates
  // the customer by email, returning the correct id for the current provider.
  private DropCustomerIdInheritedFromAnotherFunnel(FunnelFlow?: FunnelFlow) {
    let userTemporary = FunnelFlow?.UserTemporary;
    if (!userTemporary?.crmCustomerId) return;
    let currentOfferId = FunnelFlow?.SubscriptionProduct?.funnel?.offerId;
    if (currentOfferId && userTemporary.crmCustomerIdOfferId && userTemporary.crmCustomerIdOfferId !== currentOfferId) {
      this.ResetUserIdentity(userTemporary);
    }
  }

  public ValidateRegisterLimitTime() {
    if (isNaN(new Date(this.getRegistrationDateStart()).getTime())) {
      this.setRegistrationDateStart();
      return;
    }

    if (this.IsRegisterSessionExpired()) {
      this.RestartExpiredRegistration();
      return;
    }

    // The limit is measured from the last activity, not from the start of the registration:
    // every step change / reload within the window restarts the 24h countdown. The storage key
    // keeps its old name so sessions already in progress are not reset by the deploy.
    this.setRegistrationDateStart();
  }

  private IsRegisterSessionExpired(): boolean {
    let getRegistrationDateStart = new Date(this.getRegistrationDateStart());
    if (isNaN(getRegistrationDateStart.getTime())) return false;

    const getRegistrationDateStartUTC = new Date(new Date(getRegistrationDateStart).getUTCFullYear(),
      new Date(getRegistrationDateStart).getUTCMonth(), new Date(getRegistrationDateStart).getUTCDate(),
      new Date(getRegistrationDateStart).getUTCHours(), (new Date(getRegistrationDateStart).getUTCMinutes()),
      new Date(getRegistrationDateStart).getUTCSeconds());

    const registerDeadlineUTC = new Date(
      new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate(),
      new Date().getUTCHours(), (new Date().getUTCMinutes() - environment.maxRegisterSessionTimeMinutes),
      new Date().getUTCSeconds());

    return getRegistrationDateStartUTC < registerDeadlineUTC;
  }

  // An expired registration restarts silently (no popup) under the internal affiliate
  // (environment.internalRestartAffiliateId): a new Everflow click is created for the current
  // offer, storage is wiped and the page reloads with ts/clickid of that click. Tracking params
  // of the original affiliate (cid, s1, s2, sub*, ...) are dropped; prefill data is kept.
  private async RestartExpiredRegistration(): Promise<void> {
    if (this.restartingExpiredRegistration) return;
    this.restartingExpiredRegistration = true;
    this.alertService.setLoading(true, LoadingType.Default, ["Loading..."]);

    const { pathname, search } = this.GetCurrentLocation();
    const currentParams = new URLSearchParams(search);
    const offerId = currentParams.get('oid') ?? '';
    const affiliateId = environment.internalRestartAffiliateId.toString();

    // The URL carries a click different from the one of the expired session: the user arrived
    // through a new tracking link, so that affiliate keeps the attribution.
    const storedClickId = this.cypherService.DecryptObject(localStorage.getItem(user_register_storage) ?? "")?.UserEverflowEvent?.EverflowTransactionId ?? '';
    const urlClickId = currentParams.get('clickid') ?? '';
    const arrivedThroughNewLink = !!urlClickId && urlClickId !== storedClickId;

    const clickId = (offerId && !arrivedThroughNewLink) ? await this.CreateEverflowClick(offerId, affiliateId) : '';
    this.tokenService.clearStorage();

    if (!clickId) {
      this.RedirectTo(`${pathname}${search}`);
      return;
    }

    const restartParams = new URLSearchParams();
    currentParams.forEach((value, key) => {
      if (value && INTERNAL_RESTART_KEPT_PARAMS.includes(key)) restartParams.append(key, value);
    });
    restartParams.set('ts', affiliateId);
    restartParams.set('clickid', clickId);
    this.RedirectTo(`${pathname}?${restartParams.toString()}`);
  }

  // EF.click never settles for an offer Everflow does not know, hence the timeout.
  private CreateEverflowClick(offerId: string, affiliateId: string): Promise<string> {
    return new Promise<string>(resolve => {
      const timeoutId = setTimeout(() => resolve(''), INTERNAL_RESTART_CLICK_TIMEOUT_MS);
      const done = (transactionId?: string) => {
        clearTimeout(timeoutId);
        resolve(transactionId ?? '');
      };
      try {
        Promise.resolve(window.EF?.click({ offer_id: offerId, affiliate_id: affiliateId }))
          .then(done, () => done());
      } catch {
        done();
      }
    });
  }

  private GetCurrentLocation(): { pathname: string, search: string } {
    return { pathname: window.location.pathname, search: window.location.search };
  }

  private RedirectTo(url: string) {
    window.location.href = url;
  }

  private getRegistrationDateStart(): string {
    let activity: string = localStorage.getItem("registration-start-date")!;
    if (activity) {
      return this.cypherService.DecryptText(activity);
    }
    return "";
  }

  private setRegistrationDateStart() {
    localStorage.setItem("registration-start-date", this.cypherService.EncryptText(new Date().toUTCString()));
  }

  public SaveUserRegisterInStorage(FunnelFlow?: FunnelFlow, NextStep?: FunnelStep) {
    this.alertService.setLoading(false);
    if (FunnelFlow) {
      FunnelFlow!.Step = NextStep;
      this.funnelFlowSubject?.next(FunnelFlow!);
      this.tokenService.saveModel(this.cypherService.EncryptObject(FunnelFlow!), user_register_storage);
    }
  }

  public SendRegisterForm(FunnelFlow?: FunnelFlow, NextStep?: FunnelStep, hideLoading?: boolean, loadingType: LoadingType = LoadingType.Default, loadingTexts: Array<string> = ["Loading..."]): Observable<IFunnelFlowResponse> {
    // The session is otherwise only checked on step changes, so a user idle on a page past the
    // limit could still submit (even pay) with the expired session. Nothing is sent: the
    // registration restarts and the returned observable never emits (the page reloads).
    if (this.IsRegisterSessionExpired()) {
      this.RestartExpiredRegistration();
      return NEVER;
    }
    if (!hideLoading) {
      if (NextStep == FunnelStep.ThankYouPage) {
        this.alertService.setLoading(true, LoadingType.Black);
      }
      else {
        this.alertService.setLoading(true, loadingType, loadingTexts);
      }
    }
    if (FunnelFlow?.UserInfoSent?.includes(UserInfoSentType.Checkout) || FunnelFlow?.UserTemporary?.creditCard?.creditCardNumber) {
      this.EncryptPaymentData(FunnelFlow?.UserTemporary!);
    }
    FunnelFlow!.Brand = this.GetRegistrationBrand();
    if(!FunnelFlow!.UserTemporary!.status) FunnelFlow!.UserTemporary!.status = MemberAccessLevel.Partial;
    this.DropCustomerIdInheritedFromAnotherFunnel(FunnelFlow);
    // Every request keeps its payload byte-for-byte as today unless the flag is ON AND this is
    // the Step1 -> Step2 submit AND the resolved trigger is OnStep1Complete. Appending SaveUser
    // LAST is what gives REQ-09 for free: the API iterates RegisterProcesses in order, so
    // ValidateEmailWithAPI throws before SaveUser ever runs on a failed Step 1 validation.
    // isStep1Submit is cheap and synchronous, so it's checked before touching the flag observable
    // at all -- every non-Step1 POST (checkout/upsell/thank-you/etc.) keeps its original fast
    // synchronous path and never waits on /feature-flags.
    const isStep1Submit = FunnelFlow?.Step === FunnelStep.Step1 && NextStep === FunnelStep.Step2;
    const partialSaveTriggerFlagEnabled$ = isStep1Submit ? this.isPartialSaveTriggerFlagEnabled$() : of(false);
    return partialSaveTriggerFlagEnabled$.pipe(switchMap((partialSaveTriggerFlagEnabled) => {
      if (partialSaveTriggerFlagEnabled && isStep1Submit && this.ResolvePartialSaveTrigger(FunnelFlow) === PartialSaveTrigger.OnStep1Complete) {
        FunnelFlow!.Step1Completed = true;
        // RegisterProcesses may already contain SaveUser (many step1 components already submit it
        // themselves) -- dedupe so it isn't appended a second time and doesn't trigger a duplicate
        // SaveUserTemporary/SendPartialCustomerToRemarketing on the API side.
        if (!FunnelFlow!.RegisterProcesses?.includes(RegisterProcessesType.SaveUser)) {
          FunnelFlow!.RegisterProcesses = [...(FunnelFlow!.RegisterProcesses ?? []), RegisterProcessesType.SaveUser];
        }
      }
      return this.http.post<IFunnelFlowResponse>(`${environment.apiUrl}/api/funnel/registeruserstep`, FunnelFlow).pipe(map((funnelFlowResponse: IFunnelFlowResponse) => {
        FunnelFlow!.UserTemporary = {...funnelFlowResponse?.data?.userRegister, paymentType: FunnelFlow?.UserTemporary?.paymentType };
        // Stamp the offer that owns this crmCustomerId so it can be discarded if the user later
        // re-registers in a different funnel/provider.
        if (FunnelFlow!.UserTemporary?.crmCustomerId) {
          FunnelFlow!.UserTemporary.crmCustomerIdOfferId = FunnelFlow!.SubscriptionProduct?.funnel?.offerId;
        }
        FunnelFlow!.UserInfoSent = [];
        FunnelFlow!.ValidateEmailWithAPI = funnelFlowResponse?.data?.validateEmailWithAPI;
        FunnelFlow!.IsNewKonnektivePlatform = funnelFlowResponse?.data?.isNewKonnektivePlatform;
        FunnelFlow!.IsNewMemberPortal = funnelFlowResponse?.data?.isNewMemberPortal;
        if (NextStep && funnelFlowResponse?.data?.isSuccess) {
          this.SaveUserRegisterInStorage(FunnelFlow, NextStep);
        }
        return funnelFlowResponse;
      }));
    }));
  }

  public GetRegistrationBrand(): Brand {
    switch(window.location.host) {
      case "localhost:4200":
        return Brand.QA;
      case "uspfm-web-site-qa.s3-website-us-east-1.amazonaws.com":
        return Brand.QA;
      case "pfiverify.com":
        return Brand.PFIVerify;
      case "www.pfiverify.com":
        return Brand.PFIVerify;
      case "getpfiverify.com":
        return Brand.GetPFIVerify;
      case "www.getpfiverify.com":
        return Brand.GetPFIVerify;
      case "financeify.co":
        return Brand.Financeify;
      case "www.financeify.co":
        return Brand.Financeify;
      case "financeify.com":
        return Brand.Financeify;
      case "www.financeify.com":
        return Brand.Financeify;
      case "www.getfinanceify.com":
        return Brand.GetFinanceify;
      case "getfinanceify.com":
        return Brand.GetFinanceify;
      case "verifyfinancialprofile.com":
        return Brand.VerifyFinancialProfile;
      case "www.verifyfinancialprofile.com":
        return Brand.VerifyFinancialProfile;
      case "www.finsightsai.com":
        return Brand.Finsights;
      case "finsightsai.com":
        return Brand.Finsights;
      case "www.pfisimplify.com":
        return Brand.PFISimplify;
      case "pfisimplify.com":
        return Brand.PFISimplify;
      case "www.pfi-verify.com":
        return Brand.PFIVerifyDOVentures;
      case "www.myfinanceify.com":
        return Brand.MyFinanceifyDOVentures;
      case "www.financeify.net":
        return Brand.Financeify;
      case "financeify.net":
        return Brand.Financeify;
      case "www.financialinsightai.com":
        return Brand.FinancialInsight;
      case "financialinsightai.com":
        return Brand.FinancialInsight;
      // Stage:
      case "stage.pfisimplify.com":
        return Brand.PFISimplify;
      case "stage.pfiverify.com":
        return Brand.PFIVerify;  
      case "stage.financeify.co":
        return Brand.Financeify;
      case "stage.financeify.com":
        return Brand.Financeify;
      case "stage.finsightsai.com":
        return Brand.Finsights;
      case "stage.pfi-verify.com":
        return Brand.PFIVerifyDOVentures;
      case "stage.myfinanceify.com":
        return Brand.MyFinanceifyDOVentures;
      case "stage.financialinsightai.com":
        return Brand.FinancialInsight;
      default:
        return Brand.PFIVerify;
    }
  }

  private EncryptPaymentData(UserRegister: UserRegister): UserRegister {
    UserRegister!.creditCard!.creditCardNumber! = this.cypherService.EncryptText(UserRegister?.creditCard?.creditCardNumber!);
    UserRegister!.creditCard!.yearExpiration! = this.cypherService.EncryptText(UserRegister?.creditCard?.yearExpiration!);
    UserRegister!.creditCard!.monthExpiration! = this.cypherService.EncryptText(UserRegister?.creditCard?.monthExpiration!);
    UserRegister!.creditCard!.cvv! = this.cypherService.EncryptText(UserRegister?.creditCard?.cvv!);
    return UserRegister!;
  }

  public GetUSAStates(): Observable<Array<{ Code: string, Name: string }>> {
    return this.http.get<Array<{ Code: string, Name: string }>>(`./assets/funnels/usa-states.json`);
  }

  public CheckIfEmailHasBeenAlreadyValidated(FunnelFlow: FunnelFlow, Email?: string) {
    FunnelFlow.RegisterProcesses = [RegisterProcessesType.ValidateEmailWithAPI];
    let emailAlreadyValidated = FunnelFlow.FunnelMessage?.ValidatedEmails?.find(c => c.Email == (Email ? Email : FunnelFlow.UserTemporary?.email));
    if (emailAlreadyValidated) {
      if (emailAlreadyValidated?.IsValid != undefined && !emailAlreadyValidated?.IsValid) {
        this.ValidateFunnelMessage(FunnelFlow);
        return true; //Don't send form
      } else if (emailAlreadyValidated?.IsValid == undefined || emailAlreadyValidated?.IsValid) {
        FunnelFlow.RegisterProcesses = [];
      }
    }
    else {
      FunnelFlow.FunnelMessage?.ValidatedEmails?.push({ Email: (Email ? Email : FunnelFlow.UserTemporary?.email!) });
    }

    this.SaveUserRegisterInStorage(FunnelFlow, FunnelFlow?.Step);
    return false; //Send form
  }

  GetCreditCardTotalAttempts(FunnelFlow: FunnelFlow): number {
    let TotalAttempts = 0;
    for (let Card of FunnelFlow?.FunnelMessage?.CreditCardAttempts! ?? []) {
      TotalAttempts += Card.creditCardAttempts!;
    }
    return TotalAttempts;
  }

  public initBINRulesList() {
    this.binRulesService.initBINRulesList();
  }

  public CreditCardBINIsNotAllowed(CreditCardEncrypted: string, FunnelFlow: FunnelFlow): { FunnelFlow: FunnelFlow, CreditCardAllowed: boolean } {
    let bin: string = this.cypherService.DecryptText(CreditCardEncrypted).substring(0, 6);
    let binRulesValidationResponse = this.binRulesService.validateCreditCardBIN(bin, FunnelFlow);

    let CreditCardTotalAttempts: number = this.GetCreditCardTotalAttempts(FunnelFlow);
    let lastCreditCardTotalAttempt = (CreditCardTotalAttempts == (environment.MaxAttemptsCreditCard - 1));
    let creditCardAlreadyTried = FunnelFlow.FunnelMessage?.CreditCardAttempts?.find(c => c.creditCardNumber == CreditCardEncrypted);
    if (!binRulesValidationResponse.allowedBIN && binRulesValidationResponse.allowedForLastAttempt && lastCreditCardTotalAttempt && creditCardAlreadyTried) {
      FunnelFlow.UserEverflowEvent!.EverflowTransactionId = ""; // BIN allowed for last attempt: Import order but not run Everflow conversion
    } else if(!binRulesValidationResponse.allowedBIN) {
      this.reportBlockBinService.reportSaleFromFunnel(FunnelFlow, "binBlock", bin);
      this.MarkDecline(FunnelFlow.UserTemporary?.userTemporaryId!).pipe(first()).subscribe({
        next: (response: { data?: boolean }) => { },
        error: (error: string) => {
          console.log(error);
        }
      });
      return { FunnelFlow: FunnelFlow, CreditCardAllowed: false };
    }
    return { FunnelFlow: FunnelFlow, CreditCardAllowed: true };
  }

  public MarkDecline(userTemporaryId: number): Observable<{ data?: boolean }> {
    this.alertService.setLoading(true, LoadingType.Default);
    return this.http.put<{ data?: boolean }>(`${environment.apiUrl}/api/funnel/decline`, { userTemporaryId: userTemporaryId })
      .pipe(map((response: { data?: boolean }) => {
      return response;
    }));
  }

  public ValidateFunnelMessages(FunnelFlow: FunnelFlow, funnelCodeResponse: string, bin?: string) {
    FunnelFlow.FunnelMessage!.ValidateCreditCardAttempts = false;
    switch (funnelCodeResponse) {
      case FunnelCodeResponse.UserAlreadyExists.toString():
      case FunnelCodeResponse.Blacklisted.toString():
        FunnelFlow.FunnelMessage!.Code = "001";
        break;
      case FunnelCodeResponse.ImportLeadFailed.toString():
        FunnelFlow.FunnelMessage!.Code = "002";
        break;
      case FunnelCodeResponse.CreditCardFailed.toString():
        this.reportBlockBinService.reportSaleFromFunnel(FunnelFlow, "attemptFailed", bin!);
        FunnelFlow.FunnelMessage!.ValidateCreditCardAttempts = true;
        break;
      case FunnelCodeResponse.CreditCardFailedNSF.toString():
        this.reportBlockBinService.reportSaleFromFunnel(FunnelFlow, "attemptFailed", bin!);
        FunnelFlow.FunnelMessage!.ValidateCreditCardAttempts = true;
        break;
      case FunnelCodeResponse.ImportOrderFailed.toString():
        this.reportBlockBinService.reportSaleFromFunnel(FunnelFlow, "attemptFailed", bin!);
        FunnelFlow.FunnelMessage!.Code = "008";
        FunnelFlow.FunnelMessage!.ValidateCreditCardAttempts = true;
        break;
      case FunnelCodeResponse.CardBinBlockedOnCampaign.toString():
        this.reportBlockBinService.reportSaleFromFunnel(FunnelFlow, "attemptFailed", bin!);
        FunnelFlow.FunnelMessage!.ValidateCreditCardAttempts = true;
        break;
      case FunnelCodeResponse.InvalidEmail.toString():
        FunnelFlow.FunnelMessage!.Code = "010";
        let emailAlreadyValidated = FunnelFlow?.FunnelMessage?.ValidatedEmails?.find(c => c.Email == FunnelFlow.UserTemporary?.email);
        if (emailAlreadyValidated) emailAlreadyValidated!.IsValid = false;
        break;
      case FunnelCodeResponse.InvalidAddress.toString():
        FunnelFlow.FunnelMessage!.Code = "011";
        break;
      case FunnelCodeResponse.InvalidPhone.toString():
        FunnelFlow.FunnelMessage!.Code = "013";
        break;
      case FunnelCodeResponse.InvalidInformation.toString():
        FunnelFlow.FunnelMessage!.Code = "014";
        break;
      case FunnelCodeResponse.KonnektiveKkError.toString():
        FunnelFlow.FunnelMessage!.Code = "018";
        FunnelFlow.FunnelMessage!.ValidateCreditCardAttempts = false;
        break;
      case FunnelCodeResponse.ReCaptchaError.toString():
        FunnelFlow.FunnelMessage!.Code = "018";
        FunnelFlow.FunnelMessage!.ValidateCreditCardAttempts = true;
        break;
      default:
        // A card number the API refuses comes back as model validation, not as a FunnelCodeResponse,
        // so it can only be recognised here. Treated as the card rejection it is: the attempt is
        // counted like any other decline and the backend's own text is carried into the message box
        // through {additionalMessage}. The removed frontend test-card guard showed "009" for this
        // same situation, and ValidateFunnelResponse restores it after the attempt is counted.
        if (isCreditCardValidationError(funnelCodeResponse)) {
          this.reportBlockBinService.reportSaleFromFunnel(FunnelFlow, "attemptFailed", bin!);
          FunnelFlow.FunnelMessage!.AdditionalMessage = getCreditCardValidationMessage(funnelCodeResponse);
          FunnelFlow.FunnelMessage!.Code = "009";
          FunnelFlow.FunnelMessage!.ValidateCreditCardAttempts = true;
          break;
        }
        FunnelFlow.FunnelMessage!.Code = funnelCodeResponse;
    }

    this.ValidateFunnelMessage(FunnelFlow, funnelCodeResponse);
  }

  public ValidateFunnelMessage(FunnelFlow: FunnelFlow, funnelCodeResponse?: string) {
    this.GetFunnelMessage(FunnelFlow.FunnelMessage?.Code!).subscribe((FunnelMessage: IFunnelValidations) => {
      if (FunnelMessage?.Code) {
        FunnelFlow.FunnelMessage!.Message = FunnelMessage?.Message?.replace("{email}", FunnelFlow?.UserTemporary?.email!)!;
        FunnelFlow.FunnelMessage!.Message = FunnelFlow?.FunnelMessage?.Message?.replace("{additionalMessage}", FunnelFlow?.FunnelMessage?.AdditionalMessage! ?? "")!;
        if (funnelCodeResponse && funnelCodeResponse != FunnelCodeResponse.InvalidAddress.toString() && funnelCodeResponse != FunnelCodeResponse.InvalidEmail.toString() && funnelCodeResponse != FunnelCodeResponse.InvalidPhone.toString()) {
          setTimeout(() => {window.scrollTo(0, 0);}, 10);
        }
      } else {
        this.alertService.showAlert({
          Description: FunnelMessage?.Message,
          Position: AlertPosition.BottomRight,
          Type: AlertType.Error,
        });
      }
    });

    this.SaveUserRegisterInStorage(FunnelFlow, FunnelFlow?.Step);
  }

  public GetFunnelMessage(FunnelMessageCode: string): Observable<IFunnelValidations> {
    return this.GetFunnelMessagesJSON().pipe(map((funnelMessages: Array<IFunnelValidations>) => {
      let FunnelMessage: IFunnelValidations = { Message: FunnelMessageCode }
      return funnelMessages?.find(message => message.Code == FunnelMessageCode) ?? FunnelMessage;
    }));
  }

  private GetFunnelMessagesJSON(): Observable<Array<IFunnelValidations>> {
    return this.http.get<Array<IFunnelValidations>>(`./assets/funnels/funnel-messages.json`);
  }

  public ReplaceRegExp(input: string, type: string) {
    var value = "";
    if (input != null && input != "undefined") {
      switch (type) {
        case "number":
          value = input.toString().replace(/[^0-9]/g, '');
          break;
        case "lyrics":
          value = input.toString().replace('+', ' ');
          value = value.toString().replace(/[^a-zA-ZáéíóúÁÉÍÓÚü ]/g, '');
          break;
        case "email":
          value = input.toString().replace('+', ' ');
          value = value.toString().replace(/[^a-zA-Z-_.@ 0-9]/g, '');
          break;
        default:
          value = input.toString().replace('+', ' ');
          value = value.toString().replace(/[^a-zA-ZáéíóúÁÉÍÓÚü0-9 ]/g, '');
          break;
      }
    }
    return value;
  }

  public SendValidateEmailAPI(FunnelFlow?: FunnelFlow): Observable<{ data?: { isValid: boolean, validationCode: string, isAvailableResource?: boolean}} > {
    this.alertService.setLoading(true, LoadingType.Black);
    return this.http.post<{ data?: { isValid: boolean, validationCode: string, isAvailableResource?: boolean}}>(`${environment.apiUrl}/api/funnel/validateemailwithapi`, FunnelFlow)
      .pipe(map((validateEmailResponse: { data?: { isValid: boolean, validationCode: string, isAvailableResource?: boolean}}) => {
      return validateEmailResponse;
    }));
  }
  
  public SendUpdateCustomerEmail(FunnelFlow?: FunnelFlow): Observable<{ data?: boolean }> {
    return this.http.post<{ data?: boolean }>(`${environment.apiUrl}/api/funnel/updateemailcustomers`, FunnelFlow)
      .pipe(map((updateEmailCustomersResponse: { data?: boolean }) => {
      return updateEmailCustomersResponse;
    }));
  }

  setHideHeaderAndFooter(hide: boolean) {
    this.hideHeaderAndFooterSource.next(hide);
  }

  public GetIPAddress(): any {
    return this.http.get("https://api.ipify.org/?format=json");
  }

  sendPartialCustomerToRemarketingService(funnelFlow: FunnelFlow, formStep: string) {
    if(!(funnelFlow?.UserTemporary?.email?.length && funnelFlow?.UserTemporary?.firstName?.length && funnelFlow?.UserTemporary?.lastName?.length && funnelFlow?.UserTemporary?.phone?.length && funnelFlow?.SubscriptionProduct?.funnel?.offerId)) {
      return;
    }
    // Client half of Gate A -- an optimization only, RemarketingController stays authoritative
    // since cached bundles and independent deploys exist. Only the entry-time 'prepop' event is
    // suppressed under OnStep1Complete; every other formStep is sent exactly as today.
    // formStep === 'prepop' is the cheap synchronous check, so it's evaluated first -- every other
    // formStep keeps its original fast synchronous path and never waits on /feature-flags.
    if (formStep !== 'prepop') {
      this.remarketingService.addPartialCustomer(funnelFlow, formStep).subscribe((remarketingResponse: any) => {
        console.log({remarketingResponse});
      });
      return;
    }
    this.isPartialSaveTriggerFlagEnabled$().subscribe((partialSaveTriggerFlagEnabled) => {
      if (partialSaveTriggerFlagEnabled && this.ResolvePartialSaveTrigger(funnelFlow) === PartialSaveTrigger.OnStep1Complete) {
        return;
      }
      this.remarketingService.addPartialCustomer(funnelFlow, formStep).subscribe((remarketingResponse: any) => {
        console.log({remarketingResponse});
      });
    });
  }

  public HasPrePopData(user?: UserRegister): boolean {
    return !!(
      user?.firstName?.length ||
      user?.lastName?.length ||
      user?.email?.length ||
      user?.phone?.length ||
      user?.fullAddress?.length ||
      user?.addresses?.length
    );
  }
}
